import { doc, getDoc, serverTimestamp, writeBatch } from 'firebase/firestore';
import { deleteObject, ref, uploadBytes } from 'firebase/storage';
import { db, storage } from '../../../firebase';
import { createApiCall } from '../../../services/apiClient';
import type { VoiceOption, VoiceOptions } from './settingsTypes';

const DOC_ID = 'voice_options';

/** The app's voice settings page shows up to 6 voices, 3 in a line. */
export const MAX_PUBLISHED_VOICES = 6;

/** Waits for ElevenLabs, which can take some seconds for one sentence. */
const generateVoicePreview = createApiCall<
  { voiceId: string },
  { audioBase64: string; contentType: string }
>('adminGenerateVoicePreview', { timeoutMs: 60_000 });

const base64ToBytes = (base64: string): Uint8Array => Uint8Array.from(atob(base64), (char) => char.charCodeAt(0));

/**
 * Voice mode voices. The private document has all fields and is the one this page edits.
 * The public document has only the published voices without the ElevenLabs IDs; the app
 * reads it. The API reads the private document to select the voice for a call.
 */
export const voiceOptionsService = {
  async getVoiceOptions(): Promise<VoiceOptions> {
    const snap = await getDoc(doc(db, 'metadata_private', DOC_ID));
    const data = snap.data() as Partial<VoiceOptions> | undefined;
    return {
      // Voices saved before the icon field have no icon.
      voices: [...(data?.voices ?? [])].map((voice) => ({ ...voice, icon: voice.icon ?? '' })).sort((a, b) => a.order - b.order),
      defaultVoiceId: data?.defaultVoiceId ?? '',
    };
  },

  /** Saves the voices in the given order. */
  async saveVoiceOptions({ voices, defaultVoiceId }: VoiceOptions): Promise<void> {
    const published = voices.filter((voice) => voice.isPublished);
    if (published.length > MAX_PUBLISHED_VOICES) {
      throw new Error(`You can publish up to ${MAX_PUBLISHED_VOICES} voices.`);
    }
    if (published.length > 0 && !published.some((voice) => voice.id === defaultVoiceId)) {
      throw new Error('The default voice must be published.');
    }

    const ordered = voices.map((voice, index) => ({ ...voice, order: index }));
    const publicVoices = ordered
      .filter((voice) => voice.isPublished)
      .map(({ id, name, description, icon, previewPath, order }) => ({ id, name, description, icon, previewPath, order }));

    const batch = writeBatch(db);
    batch.set(doc(db, 'metadata_private', DOC_ID), { voices: ordered, defaultVoiceId, updatedAt: serverTimestamp() });
    batch.set(doc(db, 'metadata', DOC_ID), { voices: publicVoices, defaultVoiceId, updatedAt: serverTimestamp() });
    await batch.commit();
  },

  /**
   * Has the API speak the preview sentence with the voice and uploads the audio.
   * Each preview gets a new path, so the app does not play an old cached file.
   *
   * @returns The Storage path of the new preview.
   */
  async generatePreview(voice: VoiceOption): Promise<string> {
    const { audioBase64, contentType } = await generateVoicePreview({ voiceId: voice.voiceId });
    const path = `voice_previews/${voice.id}/${Date.now()}.mp3`;
    await uploadBytes(ref(storage, path), base64ToBytes(audioBase64), { contentType });
    return path;
  },

  /** Deletes a preview that no voice uses any more. A failure only leaves an unused file. */
  async deletePreview(path: string): Promise<void> {
    if (!path) return;
    try {
      await deleteObject(ref(storage, path));
    } catch (err) {
      console.warn('Failed to delete old voice preview:', err);
    }
  },
};
